import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Input, Row, Col, Empty, Skeleton, Typography, Tag, Pagination } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import company from '../../services/company';
import './index.css';
import SearchBar from '../../components/SearchBar';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import AppHeader from '../../components/Header';
import { TeamAssistantWidget } from '../../components/assistant';
import env from '../../env';
import { SEARCH_STATE_KEY, SEARCH_LOB_KEY, SEARCH_TYPE_KEY } from './../../constants/searchConstants';
import { htmlToPlainText, renderTextWithSearchHighlight } from '../../utils/highlightSearchTerm';

const PAGE_SIZE_OPTIONS = [12, 24, 36, 50];
const DEFAULT_PAGE_SIZE = 24;

const parsePageSize = (value) => {
  const n = parseInt(value, 10);
  return PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE;
};

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

const CompanyCardSkeleton = () => (
<div className="landing-company-card">
        <div className="landing-company-card-content">
          
          <div className="landing-company-details">
            <Skeleton active paragraph={{ rows: 5 }} />
          </div>
        </div>
      </div>


);

const LandingPage = () => {
  const { currentUser } = useSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    current: parseInt(searchParams.get('page'), 10) || 1,
    pageSize: parsePageSize(searchParams.get('per_page')),
    total: 0,
  });
  const [pageFind, setPageFind] = useState('');
  const companiesSectionRef = useRef(null);

  // Update the initialization effect
  useEffect(() => {
    // First check URL parameters from company details page
    if (searchParams.get('searchByLob') === 'true' || searchParams.get('searchByAo') === 'true') {
      localStorage.setItem(SEARCH_TYPE_KEY, 'lob');
      if (searchParams.get('searchByAo') === 'true') {
        const params = new URLSearchParams(searchParams);
        params.delete('searchByAo');
        params.set('searchByLob', 'true');
        setSearchParams(params, { replace: true });
      }
      return;
    }

    const savedState = localStorage.getItem(SEARCH_STATE_KEY);
    const savedLob = localStorage.getItem(SEARCH_LOB_KEY);

    if ((savedState || savedLob) && !searchParams.toString()) {
      const params = new URLSearchParams();
      if (savedState) params.set('state', savedState);
      if (savedLob) params.set('line_of_business', savedLob);
      params.set('searchByLob', 'true');
      params.set('page', '1');
      setSearchParams(params);
    }
  }, [searchParams]);

  // Construct query parameters
  const getQueryString = () => {
    const page = searchParams.get('page') || '1';
    const perPage = parsePageSize(searchParams.get('per_page'));
    const state = searchParams.get('state');
    const lineOfBusiness = searchParams.get('line_of_business');
    const searchByLob = searchParams.get('searchByLob');

    return `?page=${page}&per_page=${perPage}${
      state ? `&state=${state}` : ''}${
      lineOfBusiness ? `&line_of_business=${encodeURIComponent(lineOfBusiness)}` : ''}${
      searchByLob ? `&searchByLob=true` : ''}${
      searchParams.get('query') ? `&query=${encodeURIComponent(searchParams.get('query'))}` : ''}`;
  };

  // React Query hook
  const { data, isLoading, error } = useQuery({
    queryKey: ['companies', searchParams.toString()],
    queryFn: async () => {
      const response = await company.get(getQueryString());
      setPagination({
        current: response.data.meta.current_page,
        pageSize: response.data.meta.per_page,
        total: response.data.meta.total,
      });
      return response.data;
    },
    keepPreviousData: true // Keep previous data while fetching new data
  });

  const handleSearch = ({ state, lineOfBusiness, searchType }) => {
    const params = new URLSearchParams(searchParams); // Preserve existing params
    params.set('page', '1');
    
    if (state) {
      params.set('state', state);
    } else {
      params.delete('state');
    }
    
    if (lineOfBusiness) {
      params.set('line_of_business', lineOfBusiness);
    } else {
      params.delete('line_of_business');
    }

    if (state || lineOfBusiness) {
      params.set('searchByLob', 'true');
    } else {
      params.delete('searchByLob');
    }
    params.delete('searchByAo');

    setSearchParams(params);
  };

  const handlePageChange = (page, pageSize) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    if (pageSize) {
      params.set('per_page', String(parsePageSize(pageSize)));
    }
    setSearchParams(params);
  };

  const handlePageSizeChange = (_current, size) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    params.set('per_page', String(parsePageSize(size)));
    setSearchParams(params);
  };

  const pageSize = parsePageSize(searchParams.get('per_page'));
  const filterState = searchParams.get('state');
  const filterLob = searchParams.get('line_of_business');
  const hasFilters = Boolean(filterState || filterLob);

  const resultsSubtitle = (() => {
    if (filterState && filterLob) return `${filterLob} in ${filterState}`;
    if (filterState) return `Licensed in ${filterState}`;
    if (filterLob) return `Line of business: ${filterLob}`;
    return 'Authorized carriers in the market directory';
  })();

  const pageFindTerm = pageFind.trim();

  const pageFindMatchCount = useMemo(() => {
    if (!pageFindTerm || !data?.data?.length) return 0;
    const q = pageFindTerm.toLowerCase();
    return data.data.filter((co) => {
      const plain = co.general_info ? htmlToPlainText(co.general_info) : '';
      return co.name.toLowerCase().includes(q) || plain.toLowerCase().includes(q);
    }).length;
  }, [pageFindTerm, data]);

  useEffect(() => {
    if (!pageFindTerm || !companiesSectionRef.current) return;
    const first = companiesSectionRef.current.querySelector('.page-find-highlight');
    if (first) {
      first.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [pageFindTerm, data, pagination.current]);

  const CompanyCard = ({ company }) => {
    const plainInfo = company.general_info ? htmlToPlainText(company.general_info) : '';
    const nameMatches =
      pageFindTerm && company.name.toLowerCase().includes(pageFindTerm.toLowerCase());
    const infoMatches =
      pageFindTerm && plainInfo.toLowerCase().includes(pageFindTerm.toLowerCase());
    const cardMatchesFind = !pageFindTerm || nameMatches || infoMatches;

    return (
    <Link
      to={`/company/${company.slug}?${searchParams.toString()}`}
      state={{ fromSearch: true }}
      className="landing-company-card-link"
    >
      <article
        className={`landing-company-card ${company.sponsored === '1' ? 'sponsor-card' : ''}${
          pageFindTerm && !cardMatchesFind ? ' landing-company-card--find-dim' : ''
        }`}
      >
        <div className="landing-company-card-content">
          <div className="landing-company-details">
            <header className="landing-company-card__head">
              <h3 className="landing-company-name">
                {renderTextWithSearchHighlight(company.name, pageFindTerm, 'page-find-highlight')}
              </h3>
              {company.sponsored === '1' ? (
                <Tag className="landing-company-card__sponsor" color="blue">
                  Featured
                </Tag>
              ) : null}
            </header>

            {/* States Section - commented out
            {company.states && company.states.length > 0 && (
              <div className="company-tags">
                {company.states.slice(0, 10).map(state => (
                  <Tag color="blue" key={state.id}>
                    {state.name}
                  </Tag>
                ))}
                {company.states.length > 10 && <Tag color="gray" style={{padding: 0, margin: 0, backgroundColor: '#fff', color: '#000', fontWeight: 600}}> +{company.states.length - 10} more </Tag>}
              </div>
            )}
            */}

            {/* Line of Business Section - commented out
            {company.line_of_businesses && company.line_of_businesses.length > 0 && (
              <div className="company-tags" style={{ marginTop: '8px' }}>
                {company.line_of_businesses.slice(0, 10).map(lob => (
                  <Tag color="green" key={lob.id}>
                    {lob.name}
                  </Tag>
                ))}
                {company.line_of_businesses.length > 10 && <Tag color="gray" style={{ margin: 0, padding: 0, backgroundColor: '#fff', color: '#000', fontWeight: 600}}> +{company.line_of_businesses.length - 10} more</Tag>}
              </div>
            )}
            */}

            {/* General Details */}
            {company.general_info && (
              <div className="company-general-info">
                {pageFindTerm ? (
                  <div className="general-info-preview">
                    {renderTextWithSearchHighlight(plainInfo, pageFindTerm, 'page-find-highlight')}
                  </div>
                ) : (
                  <div
                    className="general-info-preview"
                    dangerouslySetInnerHTML={{ __html: company.general_info }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </article>
    </Link>
    );
  };

  return (
    <>
      <AppHeader />
      <div className="landing-container">
        <SearchBar 
          onSearch={handleSearch} 
          showTitle={true}
        />

        <TeamAssistantWidget
          mode="floating"
          size="mini"
          currentUser={currentUser}
          title="Assistant"
          loginPath="/login"
        />

        <div className="companies-section" ref={companiesSectionRef}>
          <div className="landing-results-bar">
            <div className="landing-results-bar__main">
              <h2 className="landing-results-bar__title">
                {hasFilters ? 'Search results' : 'Featured companies'}
              </h2>
              <p className="landing-results-bar__subtitle">{resultsSubtitle}</p>
            </div>
            <div className="landing-results-bar__tools">
              <div className="landing-page-find-box">
                <label className="landing-page-find-box__label" htmlFor="landing-page-find">
                  Find on page
                </label>
                <div
                  className={`landing-page-find-box__field${
                    pageFindTerm ? ' landing-page-find-box__field--active' : ''
                  }`}
                >
                  <SearchOutlined className="landing-page-find-box__icon" aria-hidden />
                  <Input
                    id="landing-page-find"
                    allowClear
                    bordered={false}
                    placeholder="Highlight matches…"
                    value={pageFind}
                    onChange={(e) => setPageFind(e.target.value)}
                    className="landing-page-find-box__input"
                    aria-label="Find text on this page"
                  />
                  {pageFindTerm && (
                    <span className="landing-page-find-box__count" aria-live="polite">
                      {pageFindMatchCount} match{pageFindMatchCount === 1 ? '' : 'es'}
                    </span>
                  )}
                </div>
              </div>
              {!isLoading && pagination.total > 0 && (
                <div className="landing-results-bar__meta" aria-live="polite">
                  <span className="landing-results-bar__stat">
                    <span className="landing-results-bar__stat-value">
                      {pagination.total.toLocaleString()}
                    </span>
                    <span className="landing-results-bar__stat-label">
                      {pagination.total === 1 ? 'Carrier' : 'Carriers'}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {isLoading ? (
            <Row gutter={[24, 24]}>
              {[...Array(pageSize)].map((_, i) => (
                <Col xs={24} sm={24} md={12} lg={8} key={i}>
                  <CompanyCardSkeleton />
                </Col>
              ))}
            </Row>
          ) : data?.data.length > 0 ? (
            <>
              <Row gutter={[24, 24]}>
                {data.data.map(company => (
                  <Col xs={24} sm={24} md={12} lg={8} key={company.id}>
                    <CompanyCard company={company} />
                  </Col>
                ))}
              </Row>
              <div className="pagination-container landing-results-pagination">
                <Pagination
                  current={pagination.current}
                  total={pagination.total}
                  pageSize={pagination.pageSize || pageSize}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  showSizeChanger
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageSizeChange}
                  showTotal={(total, range) =>
                    `${range[0]}–${range[1]} of ${total.toLocaleString()}`
                  }
                  size="small"
                  className="companies-pagination"
                />
              </div>
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div className="empty-state">
                  <Title level={4}>No Companies Found</Title>
                  <Text type="secondary">
                    {(searchParams.get('state') || searchParams.get('line_of_business'))
                      ? "We couldn't find any companies matching your search criteria" 
                      : "No companies are available at the moment"}
                  </Text>
                </div>
              }
            />
          )}
        </div>
      </div>
    </>
  );
};

export default LandingPage; 